import * as React from "react";

import { cn } from "../../lib/utils";
import { Box } from "../../components/ui/box";
import { Button } from "../../components/ui/button";
import { Icon, type IconName } from "../../components/ui/icon";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Text } from "../../components/ui/text";
import {
  Popover,
  PopoverPopup,
  PopoverTrigger,
} from "../../components/ui/popover";
import type { Omittable } from "../../lib/props";

/** A single navigable group in the group navigator popover. */
export type DataTableGroupNavItem = Omittable<{
  key: string;
  /** Plain-text group label (the same string the header falls back to). */
  label: React.ReactNode;
  count?: number | string;
}>;

/**
 * Where a group-navigator action jumps to. `group` scrolls a specific group's
 * header to the top of the viewport; `top`/`bottom` jump to the first/last
 * group.
 */
export type DataTableGroupNavTarget =
  | { type: "group"; key: string }
  | { type: "top" }
  | { type: "bottom" };

export type DataTableGroupNavProps = Omittable<{
  /** Zero-based ordinal of THIS group among all groups. */
  index: number;
  /** Total number of groups. */
  total: number;
  /** Ordered list of every group (for the navigator popover). */
  items: DataTableGroupNavItem[];
  /** Key of the group this navigator belongs to (highlighted in the list). */
  currentKey: string;
  /** Scroll the shared container to the requested target. */
  onNavigate: (target: DataTableGroupNavTarget) => void;
}>;

// Navigator list, shared by both chevron popovers.
function NavRow({
  icon,
  label,
  count,
  selected,
  onClick,
}: Omittable<{
  icon?: IconName;
  label: React.ReactNode;
  count?: number | string;
  selected?: boolean;
  onClick: () => void;
}>) {
  return (
    <Box
      as="button"
      onClick={onClick}
      direction="row"
      align="center"
      gap="sm"
      paddingX="sm"
      paddingY="2xs"
      rounded="sm"
      fill="width"
      // A nav row always renders inside the navigator's PopoverPopup, so its
      // surface is fixed: popover, never the table's own surface.
      hoverBackground
      className={selected ? "bg-wash-selected" : undefined}
    >
      {icon ? <Icon name={icon} size="sm" color="muted" /> : null}
      <Box flex={1} minWidth={0}>
        <Text size="sm" truncate weight={selected ? "medium" : undefined}>
          {label}
        </Text>
      </Box>
      {count !== undefined ? (
        <Text size="xs" variant="muted">
          {count}
        </Text>
      ) : null}
    </Box>
  );
}

function NavList({
  items,
  currentKey,
  onGo,
}: {
  items: DataTableGroupNavItem[];
  currentKey: string;
  onGo: (target: DataTableGroupNavTarget) => void;
}) {
  return (
    <Box direction="column">
      <NavRow
        icon="chevrons-up"
        label="Jump to top"
        onClick={() => onGo({ type: "top" })}
      />
      <Box marginY="2xs" background="border" height={1} />
      <ScrollArea className="max-h-72">
        {items.map((item, i) => (
          <NavRow
            key={`${item.key}-${i}`}
            label={item.label}
            count={item.count}
            selected={item.key === currentKey}
            onClick={() => onGo({ type: "group", key: item.key })}
          />
        ))}
      </ScrollArea>
      <Box marginY="2xs" background="border" height={1} />
      <NavRow
        icon="chevrons-down"
        label="Jump to bottom"
        onClick={() => onGo({ type: "bottom" })}
      />
    </Box>
  );
}

// Chevron + count trigger that opens the navigator popover.
function NavChevron({
  icon,
  count,
  disabled,
  label,
  items,
  currentKey,
  onNavigate,
}: {
  icon: IconName;
  count: number;
  disabled: boolean;
  label: string;
  items: DataTableGroupNavItem[];
  currentKey: string;
  onNavigate: (target: DataTableGroupNavTarget) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const go = React.useCallback(
    (target: DataTableGroupNavTarget) => {
      setOpen(false);
      onNavigate(target);
    },
    [onNavigate],
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        disabled={disabled}
        render={
          <Button
            variant="ghost"
            size="xs"
            disabled={disabled}
            aria-label={label}
          />
        }
      >
        <Icon name={icon} size="sm" color="muted" />
        <Text size="xs" variant="muted">
          {count}
        </Text>
      </PopoverTrigger>
      <PopoverPopup align="end" className="w-60">
        <NavList items={items} currentKey={currentKey} onGo={go} />
      </PopoverPopup>
    </Popover>
  );
}

/**
 * Group navigator — the up/down chevron cluster shown to the right of a sticky
 * group header's label. Each chevron carries a count (groups above / below) and
 * opens a popover that lists every group plus jump-to-top / jump-to-bottom.
 *
 * On desktop the cluster is hidden until the group header is hovered (the
 * sticky header carries the `group/gh` scope). On mobile (below the `md`
 * breakpoint — the same cutoff as `useIsMobile`) it is always visible, since
 * CSS hover never fires there. Pure CSS, no JS state — mirrors the table's
 * existing hover-revealed row actions / selection checkbox.
 */
export function DataTableGroupNav({
  index,
  total,
  items,
  currentKey,
  onNavigate,
}: DataTableGroupNavProps) {
  const above = index;
  const below = total - 1 - index;

  return (
    <Box opacity={100}
      direction="row"
      align="center"
      gap="2xs"
      shrink={false}
      className={cn(
        // Mobile: always visible. Desktop (md+): hidden until the group header
        // is hovered. Toggles pointer-events too so the hidden cluster never
        // intercepts clicks.
        "",
        "md:opacity-0 md:pointer-events-none md:transition-opacity",
        "md:group-hover/gh:opacity-100 md:group-hover/gh:pointer-events-auto",
      )}
    >
      <NavChevron
        icon="chevron-up"
        count={above}
        disabled={above === 0}
        label="Groups above"
        items={items}
        currentKey={currentKey}
        onNavigate={onNavigate}
      />
      <NavChevron
        icon="chevron-down"
        count={below}
        disabled={below === 0}
        label="Groups below"
        items={items}
        currentKey={currentKey}
        onNavigate={onNavigate}
      />
    </Box>
  );
}
